import { type ReactNode } from "react";

import { BTText } from "@btnet/core-components";
import type { CustomFieldAssociatedType } from "@btnet/services-btnet/handlers/models/customFieldAssociatedType";
import type { TextCustomFieldDefinition } from "@btnet/services-btnet/handlers/models/textCustomFieldDefinition";
import type { TextCustomFieldValue } from "@btnet/services-btnet/handlers/models/textCustomFieldValue";

import { friendlyDefaultDisplayValue } from "utilities/helpers";

import { BTTextArea } from "commonComponents/btWrappers/BTTextArea/BTTextArea";
import {
    defaultRenderAgGridCell,
    type ICustomFieldTypeHandler,
} from "commonComponents/entity/customField/utils/customFieldValues.util";

export const TextCustomFieldHandler: ICustomFieldTypeHandler<
    string,
    TextCustomFieldValue,
    TextCustomFieldDefinition
> = {
    getValue(field): string | null {
        return field.value || "";
    },

    setValue(field, value): TextCustomFieldValue {
        return { ...field, value: value ?? "" };
    },

    createValue(
        customFieldId: number,
        associatedType: CustomFieldAssociatedType,
        associatedTypeId: number,
        value: string | null,
    ): TextCustomFieldValue {
        return {
            dataType: "Text",
            customFieldId,
            customFieldValueId: 0,
            associatedType,
            associatedTypeId,
            value: value ?? "",
        };
    },

    compare(valueA: string | null, valueB: string | null): number {
        const a = valueA ?? "";
        const b = valueB ?? "";
        // Case-insensitive alphabetical comparison
        return a.localeCompare(b, undefined, { sensitivity: "base" });
    },

    renderDisplay(value: string | null): ReactNode {
        const valueToDisplay = value === "" || !value ? friendlyDefaultDisplayValue : value;

        return <BTText size="normal-md" text={valueToDisplay} />;
    },

    renderEditor(
        value: string | null,
        customFieldId: number,
        onChange: (newValue: string) => void,
        onBlur: () => void,
        autoFocus?: boolean,
    ): ReactNode {
        return (
            <BTTextArea
                id={`textCustomFieldEditor-${customFieldId}`}
                data-testid="textCustomFieldEditor"
                value={value ?? ""}
                readOnly={false}
                onChange={(_id, value) => onChange(value)}
                onBlur={(_id, _touched, blurValue) => {
                    if (blurValue !== undefined) {
                        onChange(blurValue);
                    }
                    onBlur();
                }}
                placeholder={friendlyDefaultDisplayValue}
                formMode="click-to-edit"
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        onChange((e.target as HTMLTextAreaElement).value);
                        (e.target as HTMLTextAreaElement).blur(); // Force blur to unfocus the field
                    }
                }}
                blurOnChange={false}
                autoResize="height"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={autoFocus}
            />
        );
    },

    getAgGridCellEditor() {
        // Use AG-Grid's built-in text editor for text fields
        return "agTextCellEditor";
    },

    renderAgGridCell(context): ReactNode {
        return defaultRenderAgGridCell(context);
    },
};
